import { FC, useRef, useState } from "react";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import { useI18n } from "../../hooks/useTranslations";
import { useManufacturersApi } from "../../http/manufacturers/api";
import { Manufacturer } from "../../http/manufacturers/types";
import { PaginationQueryWithFields } from "../../types";
import { isWithinLast48h } from "../../types/utils";
import Header from "../ui/Header";
import { Action, getDeleteAction, getEditAction, getHistoryAction } from "../ui/ListItemMenu";
import NewItemIcon from "../ui/NewItemIcon";
import Panel from "../ui/Panel";
import Table from "../ui/Table";
import CreateManufacturerButton from "./CreateManufacturerButton";
import DeleteManufacturerDialog from "./DeleteManufacturerDialog";
import EditManufacturerDialog from "./EditManufacturerDialog";
import ManufacturerHistoryDialog from "./ManufacturerHistoryDialog";

const MANUFACTURER_FIELDS = ["id", "createdAt", "name", "versionNumber"] as const;
type ManufacturerField = (typeof MANUFACTURER_FIELDS)[number];
type ManufacturerSearch = Pick<Manufacturer, ManufacturerField>;

const Manufacturers: FC = () => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const manufacturersApi = useManufacturersApi();
  const filters = useListFilters({ isSearchable: true });
  const debouncedValue = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());
  const [itemToEdit, setItemToEdit] = useState<ManufacturerSearch>();
  const [itemToDelete, setItemToDelete] = useState<ManufacturerSearch>();
  const [itemToShowVersions, setItemToShowVersions] = useState<ManufacturerSearch>();

  const query: PaginationQueryWithFields<ManufacturerSearch> = {
    ...filters.state,
    q: debouncedValue,
    fields: MANUFACTURER_FIELDS,
  };

  const result = manufacturersApi.useAdminManufacturers(query, aborter.current.signal);
  const [data] = useDataInitializer(result.data, result.error);

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  const getRowActions = (item: ManufacturerSearch): Action[] => {
    const actions: Action[] = [];

    if (userSettings.permissions.manufacturers.update) {
      actions.push(getEditAction(i18n, () => setItemToEdit(item)));
    }

    actions.push(getHistoryAction(i18n, () => setItemToShowVersions(item)));

    if (userSettings.permissions.manufacturers.delete) {
      actions.push(getDeleteAction(i18n, () => setItemToDelete(item), true));
    }

    return actions;
  };

  return (
    <>
      <Header title={i18n.t.manufacturers.plural} />
      <Panel>
        <Table<ManufacturerSearch>
          filters={filters}
          searchOptions={{
            onChange: () => {
              aborter.current.abort();
              aborter.current = new AbortController();
            },
          }}
          buttons={{
            main: !!userSettings.permissions.manufacturers.insert && <CreateManufacturerButton />,
          }}
          head={
            <>
              <Table.Th title={i18n.t.common.name} fieldName="manufacturer.name" order={orderOptions} />
              <th></th>
            </>
          }
          onRowClick={(item, event) => {
            event.preventDefault();
            setItemToEdit(item);
          }}
          renderRow={(item) => (
            <>
              <td>
                {isWithinLast48h(item.createdAt) && <NewItemIcon />}
                {item.name}
              </td>
            </>
          )}
          rowActions={getRowActions}
          error={result.error}
          items={data?.items}
          total={data?.total}
        />
      </Panel>

      {!!itemToDelete && !!itemToDelete.id && (
        <DeleteManufacturerDialog manufacturerId={itemToDelete.id} onClose={() => setItemToDelete(undefined)} />
      )}

      {!!itemToEdit && !!itemToEdit.id && (
        <EditManufacturerDialog manufacturerId={itemToEdit.id} onClose={() => setItemToEdit(undefined)} />
      )}

      {!!itemToShowVersions && (
        <ManufacturerHistoryDialog
          itemToShowVersionsId={itemToShowVersions.id}
          itemVersionToShowVersions={itemToShowVersions.versionNumber}
          onClose={() => setItemToShowVersions(undefined)}
        />
      )}
    </>
  );
};

export default Manufacturers;
